/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-viewer canvas: view/state objects and coordinate helpers
// (core-viewer.js 35-57, 1564-1575, 4611-4617, 169-177).
// The prototype kept `view` and `state` as module globals mutated in place by
// every handler; here they are plain mutable objects created per viewer instance
// and threaded explicitly. Helper bodies are verbatim with (view, duration)
// made explicit arguments.

export const DEFAULT_VIEW_SECONDS = 6
export const MIN_VIEW_SECONDS = 2

// core-viewer.js 35-40
export const CWT_BANDS = {
  full: { label: "Full band", min: 20, max: 300 },
  murmur: { label: "35-250 Hz", min: 35, max: 250 },
  low: { label: "35-200 Hz", min: 35, max: 200 },
  high: { label: "200-300 Hz", min: 200, max: 300 },
}

// core-viewer.js 57
export function createView(duration) {
  return { start: 0, width: Math.min(DEFAULT_VIEW_SECONDS, duration || DEFAULT_VIEW_SECONDS) }
}

// core-viewer.js 41-48. `playbackFilter` is carried for prototype-state parity
// only: the listening filters live in audio/ and nothing in the canvas layer
// reads it.
export function createViewerState() {
  return {
    layers: { murmur: false, s1s2: false, regions: false, noise: false },
    // dev.18 Coherent Energy research state. `energyGuideMode` is always
    // "coherent" in the upstream template (setEnergyGuideMode('current') has no
    // caller); the field is kept so the verbatim drawWave branch reads it.
    energyGuideMode: "coherent",
    energyBand: "s100",
    energyBandInitialized: false,
    energyBandAutoSelected: false,
    // handoff v0.8.3 Energy research toggle (Overlap check); carried so the
    // state object matches upstream. The canvas layer does not read it.
    markerOverlapPreview: false,
    // handoff v0.8.3 visible-window Loop. `loopRange` is {start,end,revision};
    // it is only authoritative while `revision === loopRevision` (see
    // loop.js activeLoopBounds). The remaining flags are the loop
    // controller's seek/edit transaction state.
    windowLoop: false,
    loopRange: null,
    loopRevision: 0,
    loopViewportEditing: false,
    loopEditResumePlayback: false,
    loopResumeAfterSeek: false,
    loopSeekPending: false,
    loopSeekTarget: null,
    playbackFilter: "off",
    tooltipsEnabled: true,
    waveformView: "clean",
    researchTab: "frequency",
    cwtBand: "full",
  }
}

// core-viewer.js 1564-1568
export function clampView(view, duration) {
  const minWidth = Math.min(MIN_VIEW_SECONDS, duration)
  view.width = Math.max(minWidth, Math.min(duration, view.width))
  view.start = Math.max(0, Math.min(duration - view.width, view.start))
}

// core-viewer.js 1569-1570
export function tToX(view, t, pL, pW) {
  return pL + ((t - view.start) / view.width) * pW
}
export function xToT(view, x, pL, pW) {
  return view.start + ((x - pL) / pW) * view.width
}

// core-viewer.js 1571-1575
export function visibleRange(view, duration, len) {
  const i0 = Math.max(0, Math.floor((view.start / duration) * len) - 1)
  const i1 = Math.min(len, Math.ceil(((view.start + view.width) / duration) * len) + 1)
  return [i0, i1]
}

// core-viewer.js 4611-4617 (redraw is the caller's job here)
export function setZoomCentered(view, duration, newWidth) {
  const center = view.start + view.width / 2
  view.width = newWidth
  view.start = center - view.width / 2
  clampView(view, duration)
}

// core-viewer.js 169-177 — keep the playhead inside a 12%-88% guard band.
export function ensurePlaybackVisible(view, duration, t) {
  if (!Number.isFinite(t) || view.width >= duration) return false
  const leftGuard = view.start + view.width * 0.12
  const rightGuard = view.start + view.width * 0.88
  if (t >= leftGuard && t <= rightGuard) return false
  view.start = t - view.width * 0.2
  clampView(view, duration)
  return true
}
